import React, { useState } from 'react'

import type { TChatRating } from '@homepal/api-contract'

import Box from 'components_new/atoms/Box'
import Chip from 'components_new/atoms/Chip'
import Icon from 'components_new/atoms/Icon'
import IconButton from 'components_new/atoms/IconButton'
import Text from 'components_new/atoms/Text'
import Tooltip from 'components_new/atoms/Tooltip'

import SearchField from 'components_new/molecules/SearchField'
import SimpleTable from 'components_new/molecules/SimpleTable'

import TranscriptPanel from 'components_new/organisms/internal/SimChats/TranscriptPanel'
import { Highlighted } from 'components_new/organisms/StandardSearch/utils'

import PageLayout from 'components_new/templates/PageLayout'

import { useTranslation } from 'language/translator'
import {
  useInternalSimChat,
  useInternalSimChats
} from 'redux/hooks/internal/SimChats'
import { SimChatSummary } from 'redux/selectors/internal/SimChats'

import { getParsedDate } from 'utils/dateParser'

const PAGE_SIZE = 200

/** A match on the chat's title, customer, table or user. */
const matches = (chat: SimChatSummary, search: string) => {
  const query = search.toLowerCase()

  return [
    chat.title,
    chat.customer_name ?? '',
    chat.table_name ?? '',
    chat.account_email ?? ''
  ].some((value) => value.toLowerCase().includes(query))
}

const Ratings = (props: { chat: SimChatSummary }) => {
  const { chat } = props

  if (chat.rated_up_count === 0 && chat.rated_down_count === 0) {
    return (
      <Text color="text.secondary" variant="body2">
        –
      </Text>
    )
  }

  return (
    <Box sx={{ alignItems: 'center', display: 'flex', gap: 1.5 }}>
      {chat.rated_up_count > 0 ? (
        <Box sx={{ alignItems: 'center', display: 'flex', gap: 0.5 }}>
          <Icon color="success" fontSize="inherit" name="ThumbUpAltOutlined" />
          <Text variant="body2">{chat.rated_up_count}</Text>
        </Box>
      ) : null}
      {chat.rated_down_count > 0 ? (
        <Box sx={{ alignItems: 'center', display: 'flex', gap: 0.5 }}>
          <Icon color="error" fontSize="inherit" name="ThumbDownAltOutlined" />
          <Text variant="body2">{chat.rated_down_count}</Text>
        </Box>
      ) : null}
    </Box>
  )
}

/**
 * Every stored SIM modelling chat, newest activity first, with the user's
 * ratings. Opening a row shows the whole conversation.
 */
const SimChatsPage = () => {
  const { lang } = useTranslation()

  const [rating, setRating] = useState<TChatRating | undefined>(undefined)
  const [search, setSearch] = useState('')
  const [selectedId, setSelectedId] = useState<string | null>(null)

  const {
    chats, totalCount, loading, refresh
  } = useInternalSimChats({
    rating,
    limit: String(PAGE_SIZE)
  })

  const selected = useInternalSimChat(selectedId)

  const filtered = chats.filter((chat) => matches(chat, search))

  const toggleRating = (value: TChatRating) =>
    setRating((current) => (current === value ? undefined : value))

  return (
    <PageLayout
      topbar={{
        breadcrumbs: [{ title: 'Admin' }, { title: 'SIM-chattar' }]
      }}
      variant="static"
    >
      <Box
        sx={{
          alignItems: 'center',
          display: 'flex',
          flexWrap: 'wrap',
          gap: 1,
          mb: 1
        }}
      >
        <SearchField
          onClear={() => setSearch('')}
          onSearch={setSearch}
          placeholder={'Sök chatt, kund, tabell eller användare'}
          size="small"
          value={search}
        />
        <Chip
          color={rating === 'down' ? 'primary' : 'default'}
          icon={<Icon fontSize="small" name="ThumbDownAltOutlined" />}
          label="Dåliga svar"
          onClick={() => toggleRating('down')}
          size="small"
          variant={rating === 'down' ? 'filled' : 'outlined'}
        />
        <Chip
          color={rating === 'up' ? 'primary' : 'default'}
          icon={<Icon fontSize="small" name="ThumbUpAltOutlined" />}
          label="Bra svar"
          onClick={() => toggleRating('up')}
          size="small"
          variant={rating === 'up' ? 'filled' : 'outlined'}
        />
        <Box sx={{ flex: 1 }} />
        <Text color="text.secondary" variant="body2">
          {totalCount > chats.length
            ? `Visar ${chats.length} av ${totalCount} chattar`
            : `${totalCount} chattar`}
        </Text>
        <Tooltip title="Uppdatera">
          <IconButton onClick={refresh} size="small">
            <Icon fontSize="small" name="RefreshOutlined" />
          </IconButton>
        </Tooltip>
      </Box>

      <SimpleTable
        columns={[
          { title: 'Chatt', width: '30%' },
          { title: 'Kund' },
          { title: 'Tabell' },
          { title: 'Användare' },
          { align: 'right', title: 'Meddelanden' },
          { title: 'Betyg' },
          { align: 'right', title: 'Senast aktiv' }
        ]}
        emptyPlaceholder="Inga chattar ännu"
        hover={true}
        loading={loading}
        rows={filtered.map((chat) => ({
          cells: [
            <Highlighted key={0} text={chat.title} highlight={search} />,
            <Highlighted
              key={1}
              text={chat.customer_name ?? '–'}
              highlight={search}
            />,
            <Highlighted
              key={2}
              text={chat.table_name ?? '–'}
              highlight={search}
            />,
            <Highlighted
              key={3}
              text={chat.account_email ?? '–'}
              highlight={search}
            />,
            <React.Fragment key={4}>{chat.message_count}</React.Fragment>,
            <Ratings chat={chat} key={5} />,
            <React.Fragment key={6}>
              {getParsedDate(chat.updated_at, lang, { time: 'full' })}
            </React.Fragment>
          ],
          id: chat.id,
          onClick: () => setSelectedId(chat.id),
          sortValues: [
            { value: chat.title },
            { value: chat.customer_name ?? '' },
            { value: chat.table_name ?? '' },
            { value: chat.account_email ?? '' },
            { value: chat.message_count },
            { value: chat.rated_down_count * 1000 + chat.rated_up_count },
            { value: new Date(chat.updated_at) }
          ]
        }))}
        sx={{ flex: 1 }}
      />

      <TranscriptPanel
        chat={selected.chat}
        loading={selected.loading}
        onClose={() => setSelectedId(null)}
        open={selectedId !== null}
      />
    </PageLayout>
  )
}

export default SimChatsPage
